import React, {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Alert, Checkbox, Container, FormControlLabel, Typography} from '@mui/material';
import {Elements, PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js/pure';
import {determineLocation} from './assets/determineLocation';
import {paymentAppearance} from './assets/payment-appearance';
import ResponsiveAppBar from './components/responsive-app-bar';
import Footer from './components/home/Footer';
import Loading from './components/loading';
import {DarkButton} from './components/buttons';

interface CardUpdateOrder {
    receiptNumber: string;
    companyName: string;
    documentNumber: string;
    status: string;
    filingLabel: string;
    amount: number;
    schedule: {year: string; opensOn: string; window: string} | null;
    authorizationText: string;
    clientSecret: string;
    stripePublishableKey: string | null;
}

/** Lets a customer replace the saved card on a scheduled or declined order, from the signed link we emailed them. */
export default function UpdateCard() {
    const [params] = useSearchParams();
    const receiptNumber = params.get('r') ?? '';
    const token = params.get('t') ?? '';
    const [order, setOrder] = useState<CardUpdateOrder | null>(null);
    const [error, setError] = useState('');
    const [done, setDone] = useState<string | null>(null);

    useEffect(() => {
        if (!receiptNumber || !token) {
            setError('This link is incomplete. Please use the link from your email.');
            return;
        }
        fetch(`${determineLocation()}/billing/card-update/${encodeURIComponent(receiptNumber)}?t=${encodeURIComponent(token)}`, {cache: 'no-store'})
            .then(async response => {
                const body = await response.json();
                if (!response.ok) throw new Error(typeof body.message === 'string' ? body.message : 'This order could not be loaded.');
                setOrder(body.message as CardUpdateOrder);
            })
            .catch(reason => setError(reason instanceof Error ? reason.message : 'This order could not be loaded.'));
    }, [receiptNumber, token]);

    const stripePromise = useMemo(() => order?.stripePublishableKey ? loadStripe(order.stripePublishableKey) : null, [order?.stripePublishableKey]);

    return <>
        <ResponsiveAppBar showLogin={false}/>
        <Container maxWidth="sm" sx={{my: 5}}>
            <Typography variant="h4" sx={{fontWeight: 300, mb: 2}}>Update your card</Typography>
            {done ? <Alert severity="success">{done}</Alert>
                : error ? <Alert severity="error">{error}</Alert>
                : !order ? <Loading/>
                : !stripePromise ? <Alert severity="error">Card updates are temporarily unavailable. Please contact support@fcfiling.com.</Alert>
                : <>
                    <Typography sx={{fontWeight: 500}}>{order.companyName} - {order.documentNumber}</Typography>
                    <Typography sx={{mb: 2}}>{order.filingLabel} · Receipt {order.receiptNumber}</Typography>
                    {order.status === 'card_declined'
                        ? <Alert severity="warning" sx={{mb: 2}}>Your bank declined the charge for this order. Enter a different card to continue; your filing has not been submitted yet.</Alert>
                        : order.schedule ? <Alert severity="info" sx={{mb: 2}}>Your card will be saved, not charged today. We'll process your filing between {order.schedule.window} and charge your card then.</Alert> : null}
                    <Elements stripe={stripePromise} options={{clientSecret: order.clientSecret, appearance: paymentAppearance}}>
                        <UpdateCardForm order={order} token={token} onDone={setDone}/>
                    </Elements>
                </>}
        </Container>
        <Footer/>
    </>;
}

function UpdateCardForm({order, token, onDone}: {order: CardUpdateOrder; token: string; onDone: (message: string) => void}) {
    const stripe = useStripe();
    const elements = useElements();
    const [accepted, setAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!stripe || !elements || !accepted || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            const result = await stripe.confirmSetup({elements, redirect: 'if_required'});
            if (result.error) throw new Error(result.error.message ?? 'Your card could not be saved.');
            if (result.setupIntent?.status !== 'succeeded') throw new Error('Your card could not be confirmed. Please try another card.');
            const response = await fetch(`${determineLocation()}/billing/card-update/${encodeURIComponent(order.receiptNumber)}/confirm?t=${encodeURIComponent(token)}`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({setupIntent: result.setupIntent.id, accepted: true, authorizationText: order.authorizationText}),
            });
            const body = await response.json();
            if (!response.ok) throw new Error(typeof body.message === 'string' ? body.message : 'Your card could not be updated.');
            onDone(body.message?.status === 'scheduled' && order.schedule
                ? `Your card has been updated. We'll process your filing between ${order.schedule.window} and charge your new card then.`
                : 'Your card has been updated. We will now process your filing and email you when it is complete.');
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Your card could not be updated.');
            setSubmitting(false);
        }
    };

    return <form onSubmit={submit}>
        <PaymentElement/>
        <FormControlLabel sx={{mt: 2}} label={order.authorizationText}
                          control={<Checkbox checked={accepted} disabled={submitting} onChange={event => setAccepted(event.target.checked)}/>}/>
        {error ? <Alert severity="error" sx={{mt: 2}}>{error}</Alert> : null}
        <DarkButton type="submit" disabled={!stripe || !accepted || submitting} sx={{p: 2, mt: 3, width: '100%'}} disableElevation variant="contained">
            {submitting ? 'Saving…' : 'Save New Card'}
        </DarkButton>
    </form>;
}
